<template>
    <div class="view-selections-drawer">
        <div class="view-selections-drawer__free-shipping-label">
            <p>Free Shipping Over $99 | Free Returns & Exchanges</p>
        </div>

        <div class="view-selections-drawer__product-list">
            <!-- Loop through each layer -->
            <template v-for="(layerProducts, layer) in bykSelectedProducts" :key="layer">
                <!-- Loop through each subcategory (top, bottom) -->
                <template v-for="(products, subcategory) in layerProducts" :key="subcategory">
                    <!-- Loop through each product in the current subcategory -->
                    <div v-for="(product, index) in products" :key="product.id" class="view-selections-drawer__product">
                        <!-- Render product details here -->

                        <!-- Image section -->
                        <div class="view-selections-drawer__product-image-wrapper">
                            <ImageField
                                class="view-selections-drawer__product-image-wrapper-image"
                                :obj="{ 'url': product?.selectedVariantDetails?.selectedFeatureImage?.src, 'alt': product?.selectedVariantDetails?.selectedFeatureImage?.alt, 'width': 228, 'height': 285 }"
                                width-param="width"
                                sizes="300px"
                            />
                        </div>

                        <!-- Details section -->
                        <div class="view-selections-drawer__product-desc">
                            <h2 class="view-selections-drawer__product-desc-title">{{ product.title }}</h2>


                            <div class="view-selections-drawer__product-desc-price-wrapper">
                                <div :class="['view-selections-drawer__product-desc-price', {'bold': product?.selectedVariantDetails?.comparePrice > product?.selectedVariantDetails?.price}]">
                                    <span v-if="isSecondTurn">Used</span> {{formatPrice(product?.selectedVariantDetails?.price)}}
                                </div>
                                <div v-if="isPriceRange(product) && getCustomerDiscount(product.tags) <= 0" class="view-selections-drawer__product-desc-price-range">{{ isPriceRange(product) }}</div>
                                <div v-else-if="product?.selectedVariantDetails?.comparePrice > product?.selectedVariantDetails?.price" class="view-selections-drawer__product-desc-compare-price">
                                    {{formatPrice(product?.selectedVariantDetails?.comparePrice)}}
                                </div>
                            </div>

                            <div class="view-selections-drawer__product-desc-color">
                                <span>Color:</span>
                                <div class="view-selections-drawer__product-desc-color--selected">
                                    <span>{{ product?.selectedVariantDetails?.color }}</span>
                                </div>
                            </div>

                            <div class="view-selections-drawer__product-desc-options">
                                <!-- Loop through each layer -->
                                <template v-for="(layerOptions, layer) in options" :key="layer">
                                    <!-- Loop through each subcategory (top, bottom) -->
                                    <template v-for="(products, subcategory) in layerOptions" :key="subcategory">
                                        <!-- Loop through each product in the current subcategory -->
                                        <div v-for="(optionArray, optionName) in products[product.id]" :key="product.id"
                                            class="view-selections-drawer__product-desc-option">
                                            <CustomSelect v-if="Boolean(optionArray.length)" :options="optionArray"
                                                :optionPrefix="getOptionPrefix(optionName)"
                                                :callbackProps="{ optionName, layer, subcategory, productId: product.id }"
                                                :placeholder="getOptionPlaceholder(optionName)"
                                                :enableBodyOverflow="false"
                                                :enablePrefixOnOption="false"
                                                :selectedValueProp="getOptionSelectedValue(optionName, product)"
                                                :dropdown-offset-top="151.5"
                                                :enableSuffixOnOption="false" :onDropdownSelect="handleOptionChange" />
                                        </div>
                                    </template>
                                </template>
                            </div>

                            <button class="view-selections-drawer__product-desc-remove-cta" @click="() => handleRemoveProduct(product, layer, subcategory)">
                                Remove
                            </button>
                        </div>
                    </div>
                </template>
            </template>
        </div>

        <div class="view-selections-drawer__footer">
            <div class="view-selections-drawer__footer-cost-info">
                <span class="view-selections-drawer__footer-cost-title">Total cost</span>
                <span class="view-selections-drawer__footer-cost-amount">{{ formattedPrice }}</span>
            </div>
            <button
                class="btn btn--secondary btn--medium  view-selections-drawer__footer-atc"
                :disabled="isATCDisabled"
                @click="onATCClick"
            >
                ADD TO CART
            </button>

            <!-- Klarna and Afterpay tooltip section -->
            <div class="view-selections-drawer__footer-paymethods">
                <span class="view-selections-drawer__footer-paymethods-inline-container">
                    <span class="view-selections-drawer__footer-paymethods-text">{{ totalEMI }} payments of {{ amountEMI }} with</span>
                    <svg class="afterpay-image" aria-label="Afterpay">
                        <use href="#afterpay-logo"></use>
                    </svg>
                    <span class="view-selections-drawer__footer-paymethods-separator">or</span>
                    <KlarnaLogo role="image" aria-label="Klarna" />
                    <TooltipModal customClass="top">
                        <template #tooltipContent>
                            <div class="tooltip-content" role="tooltip">
                                <p>Buy items now and pay later - in {{ totalEMI }} payments, with no additional fees.</p>
                                <a :href="generateBNPLpageURL()" target="_blank">Learn more</a>
                            </div>
                        </template>
                        <button>
                            <svg class="view-selections-drawer__footer-paymethods-tooltip-image">
                                <use href="#tooltip-icon"></use>
                            </svg>
                        </button>
                    </TooltipModal>
                </span>
            </div>

        </div>
    </div>
</template>

<script setup>
import { onBeforeMount, ref, toRefs, onMounted, reactive, computed } from 'vue';
import ProductPrice from '../../pdp-first-fold/components/product-price.vue';
import ImageField from '../../global/image.vue';
import CustomSelect from '../../global/custom-select.vue';
import TooltipModal from '../../global/tooltip-modal.vue';
import KlarnaLogo from '../../pdp-first-fold/components/klarna-image.vue'; // need to check with soha for svg

const props = defineProps({
    bykSelectedProducts: {
        type: Object,
        required: true,
    },
    heading: {
        type: String,
        default: ''
    },
    onRemoveItem: {
        type: Function
    },
    onUpdatingOptions: {
        type: Function
    },
    resetBYKSelections: {
        type: Function
    },
    isSecondTurn: {
        type: Boolean,
        default: false
    },
});

const emit = defineEmits(['heading', 'component-updated']);

const { heading } = toRefs(props);

const bykSelectedProducts = reactive(JSON.parse(JSON.stringify(props.bykSelectedProducts)));


const options = ref({});
const sizeMap = ref({});

const totalEMI = 4;

onBeforeMount(() => {
    emit('heading', heading.value);
});

onMounted(() => {
    emit('component-updated');
    generateOptionsOnFirstRender();
});

const totalItems = computed(() => (
    Object.values(bykSelectedProducts)
        .flatMap(layer => Object.values(layer))
        .flat()
        .length
));

const totalPrice = computed(() => {
    return Object.values(bykSelectedProducts)
        .flatMap(layer => Object.values(layer))
        .flat()
        .reduce((total, item) => {
            const customerDiscountVal = getCustomerDiscount(item.tags);
            const variant = item.variants.find((vrnt) => vrnt.available === true && vrnt.color === item.selectedVariantDetails?.color);
            const discountedPrice = getPrice(variant.price/100, variant.compare_at_price/100, customerDiscountVal);

            return total + discountedPrice;
        }, 0);
});

const formattedPrice = computed(() => formatPrice(totalPrice.value));

const isATCDisabled = computed(() => {
    // Get all products from bykSelectedProducts
    const products = Object.values(bykSelectedProducts)
        .flatMap(layer => Object.values(layer))
        .flat();

    // Check if there are no products
    if (products.length === 0) {
        return true; // If no products are present, return true
    }

    // Check if any product is missing a variantId
    return products.some(product => !product.selectedVariantDetails?.variantId);
});

const getPrice = (price, compareAtPrice, customerDiscountVal) => {
    if (compareAtPrice > 0) {
        if (compareAtPrice * (1 - customerDiscountVal) < price) {
            return compareAtPrice * (1 - customerDiscountVal);
        } else {
            return price;
        }
    } else {
        return (price) * (1 - customerDiscountVal);
    }
};

const getOptionPrefix = (optionName) => {
    switch (optionName) {
        case 'size': return 'Size: ';
        case 'inseam': return 'Inseam: ';
        case 'outseam': return 'Outseam: ';
        default: return '';
    }
};

const getOptionPlaceholder = (optionName) => {
    switch (optionName) {
        case 'size': return 'Select Size';
        case 'inseam': return 'Select Inseam';
        case 'outseam': return 'Select Outseam';
        default: return '';
    }
};

const getOptionSelectedValue = (optionName, product) => {
    if (optionName === 'size') {
        return getModifiedSizeTitles(product?.selectedVariantDetails?.size);
    }
    return product?.selectedVariantDetails?.inseam || product?.selectedVariantDetails?.outseam || '';
};

const isPriceRange = (product) => {
    const {min_price, max_price} = product;

    if(min_price != max_price){
      return `(${formatPrice(min_price/100)} - ${formatPrice(max_price/100)})`
    }else{
      return false;
    }
};

const getCustomerDiscount = (tags) => {
    return window.customerDiscount(tags)
}

const formatPrice = (value) => {
    return window.formattedPrice(value);
};

const discountPriceLogic = (price, compareAtPrice, customerDiscountVal) => {
    if (compareAtPrice > 0) {
        if (compareAtPrice * (1 - customerDiscountVal) < price) {
            return (compareAtPrice) * (1 - customerDiscountVal)
        } else {
            return (price);
        }
    } else {
        return (price) * (1 - customerDiscountVal);
    }
};

const amountEMI = computed(() => {
    return window.formattedPrice((totalPrice?.value / totalEMI));
});

const generateOptionsOnFirstRender = () => {
    const selectedProducts = bykSelectedProducts;
    const optionsMap = {};

    if (!selectedProducts) {
        return optionsMap;
    }

    // Iterate through selected products and build the options map
    for (const layer in selectedProducts) {
        if (!optionsMap[layer]) {
            optionsMap[layer] = {};
        }
        for (const subcategory in selectedProducts[layer]) {
            if (!optionsMap[layer][subcategory]) {
                optionsMap[layer][subcategory] = {};
            }
            for (const productObj of selectedProducts[layer][subcategory]) {
                if (!optionsMap[layer][subcategory][productObj.id]) {
                    optionsMap[layer][subcategory][productObj.id] = {};
                }

                let opts = generateOptionsForSingleProduct(productObj);
                optionsMap[layer][subcategory][productObj.id] = opts;
            }
        }
    }

    options.value = optionsMap;
};

// Generate options for a single product
const generateOptionsForSingleProduct = (product) => {
    const sizeArray = product.options?.[1]?.name.toLowerCase() === 'size' ? [] : undefined;
    const inseamArray = product.options?.[2]?.name?.toLowerCase() === 'inseam' ? [] : undefined;
    const outseamArray = product.options?.[2]?.name?.toLowerCase() === 'outseam' ? [] : undefined;

    const selSizeOpt = product.selectedVariantDetails?.size;
    const selSeamOpt = product.selectedVariantDetails?.inseam || product.selectedVariantDetails?.outseam;

    for (const variant of product.variants) {
        if (!variant.available || variant.color !== product.selectedVariantDetails?.color) {
            continue; // Skip unavailable variants or mismatched color
        }

        const modifiedSizeTitle = getModifiedSizeTitles(variant.option2);

        if (variant.option2) {
            sizeMap.value[modifiedSizeTitle] = variant.option2;

            if (sizeArray?.length > -1 && !sizeArray.includes(modifiedSizeTitle) && (!selSeamOpt || selSeamOpt === variant.option3)) {
                sizeArray.push(modifiedSizeTitle);
            }
        }

        if (variant.option3) {
            if (inseamArray?.length > -1 && !inseamArray.includes(variant.option3) && (!selSizeOpt || selSizeOpt === variant.option2)) {
                inseamArray.push(variant.option3);
            }
            if (outseamArray?.length > -1 && !outseamArray.includes(variant.option3) && (!selSizeOpt || selSizeOpt === variant.option2)) {
                outseamArray.push(variant.option3);
            }
        }
    }

    const options = {
        ...(sizeArray?.length > 0 ? { size: sizeArray } : {}),
        ...(inseamArray?.length > 0 ? { inseam: inseamArray } : {}),
        ...(outseamArray?.length > 0 ? { outseam: outseamArray } : {})
    };

    return options;
};

const getModifiedSizeTitles = (value) => {
    return value ? window.formattedSize(value) : '';
}

const handleOptionChange = (selectedOption, inputProps) => {
    const { layer, optionName, productId, subcategory } = inputProps;

    const selectedProducts = bykSelectedProducts[layer]?.[subcategory];
    
    if (selectedProducts && selectedProducts.length) {
        const currentItem = selectedProducts[0];

        const lowerCaseOption = optionName.toLowerCase();
        const size = lowerCaseOption === 'size' ? sizeMap.value?.[selectedOption] : currentItem.selectedVariantDetails?.size || '';
        const inseam = lowerCaseOption === 'inseam' ? selectedOption : currentItem.selectedVariantDetails?.inseam;
        const outseam = lowerCaseOption === 'outseam' ? selectedOption : currentItem.selectedVariantDetails?.outseam;

        const updatedItem = {
            ...currentItem,
            selectedVariantDetails: {
                ...currentItem.selectedVariantDetails,
                size,
                inseam,
                outseam,
                variantId: getVariantId(
                    currentItem.variants,
                    currentItem.selectedVariantDetails?.color,
                    size,
                    inseam || outseam
                )
            }
        };

        // Update bykSelectedProducts directly
        bykSelectedProducts[layer][subcategory][0] = updatedItem;
        props.onUpdatingOptions(layer, subcategory, updatedItem);

        // Refresh render options based on selection
        const refreshedOptions = generateOptionsForSingleProduct(updatedItem);
        options.value[layer][subcategory][productId] = refreshedOptions;
    }
};

const getVariantId = (variants, color, size, seam) => {
    const title = [color, size, seam].filter(Boolean).join(' / ');
    const variant = variants.find((variant) => variant.title === title);
    return variant?.id || '';
};


const handleRemoveProduct = (product, layer, subcategory) => {
    bykSelectedProducts[layer][subcategory].splice(0, 1);
    props.onRemoveItem(product, layer, subcategory);
};

const generateBNPLpageURL = () => {
    return window.location.origin + '/pages/bnpl';
};

const onATCClick = async () => {
    const allSelectedProducts = Object.values(bykSelectedProducts)
        .flatMap(layer => Object.values(layer))
        .flat();
    const productsToAdd = allSelectedProducts.map((product) => ({ id: product?.selectedVariantDetails?.variantId, quantity: 1 }));

    window.openCart({ cartConfirmation: true });
    await window.Additem(productsToAdd, { visibility: false });

    props.resetBYKSelections();
    // Reset the products directly
    Object.keys(bykSelectedProducts).forEach(layer => {
        Object.keys(bykSelectedProducts[layer]).forEach(subcategory => {
            bykSelectedProducts[layer][subcategory] = [];
        });
    });
};
</script>
